// <s-marquee> — seamless continuous scroll of its [data-marquee-track] content.
// Clones the track once for a gapless loop and animates with the Web Animations API.
// Respects prefers-reduced-motion (no animation), pauses on hover/focus, and honours a sticky
// pause from a `--toggle` command button (WCAG 2.2.2). One component per file.
class SMarquee extends HTMLElement {
  #animation = null;
  #ac = null;
  #mql = null;
  #hovered = false;
  #focused = false;
  #userPaused = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    this.#mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.#mql.addEventListener("change", () => this.#setup(), { signal });

    this.addEventListener("mouseenter", () => { this.#hovered = true; this.#sync(); }, { signal });
    this.addEventListener("mouseleave", () => { this.#hovered = false; this.#sync(); }, { signal });
    this.addEventListener("focusin", () => { this.#focused = true; this.#sync(); }, { signal });
    this.addEventListener("focusout", (event) => {
      if (!this.contains(event.relatedTarget)) { this.#focused = false; this.#sync(); }
    }, { signal });
    document.addEventListener("visibilitychange", () => this.#sync(), { signal });

    // Commands arrive on the element named by the button's commandfor.
    this.addEventListener("command", (event) => {
      if (event.command !== "--toggle") return;
      this.#userPaused = !this.#userPaused;
      this.#updateToggle(event.source);
      this.#sync();
    }, { signal });

    this.#setup();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#animation?.cancel();
  }

  #updateToggle(button) {
    const toggle = button || this.querySelector("[data-marquee-toggle]");
    if (!toggle) return;
    toggle.setAttribute("aria-pressed", this.#userPaused ? "true" : "false");
    const label = this.#userPaused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel;
    if (label) toggle.setAttribute("aria-label", label);
    toggle.querySelector("[data-icon-pause]")?.toggleAttribute("hidden", this.#userPaused);
    toggle.querySelector("[data-icon-play]")?.toggleAttribute("hidden", !this.#userPaused);
  }

  #sync() {
    if (!this.#animation) return;
    const shouldPause = this.#userPaused || this.#hovered || this.#focused || document.hidden;
    if (shouldPause) this.#animation.pause();
    else this.#animation.play();
  }

  #setup() {
    this.#animation?.cancel();
    this.#animation = null;

    const track = this.querySelector("[data-marquee-track]");
    if (!track) return;

    // Ensure exactly two copies of the content so the loop is seamless; the copy is decorative.
    if (!track.dataset.cloned) {
      const clones = [...track.children].map((node) => {
        const clone = node.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        clone.setAttribute("inert", "");
        return clone;
      });
      track.append(...clones);
      track.dataset.cloned = "true";
    }

    if (this.#mql.matches) return;

    const distance = track.scrollWidth / 2;
    if (distance <= 0) return;

    const speed = parseFloat(this.dataset.speed) || 40;
    const durationMs = (distance / speed) * 1000;
    const isRtl = getComputedStyle(this).direction === "rtl";
    let reverse = this.dataset.direction === "right";
    if (isRtl) reverse = !reverse;
    const from = reverse ? -distance : 0;
    const to = reverse ? 0 : -distance;
    const sign = isRtl ? -1 : 1;

    this.#animation = track.animate(
      [{ transform: `translateX(${from * sign}px)` }, { transform: `translateX(${to * sign}px)` }],
      { duration: durationMs, iterations: Infinity, easing: "linear" },
    );
    this.#sync();
  }
}

if (!customElements.get("s-marquee")) {
  customElements.define("s-marquee", SMarquee);
}
